import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { identityDomain } from '@domain/identity/IdentityDomainFactory';

interface BootstrapScreenProps {
  user: any;
  message: string;
  onLogout?: () => void;
  onBootstrapSuccess: () => void;
}

/**
 * BootstrapScreen - UI for initializing the F&B Node (Owner Profile)
 */
export const BootstrapScreen: React.FC<BootstrapScreenProps> = ({ 
  user, 
  message, 
  onLogout, 
  onBootstrapSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const identityRepo = identityDomain.getRepository();

  const handleInit = async () => {
    setError(null);
    setLoading(true);
    try {
      // Using the new repository for ACID-safe profile creation
      await identityRepo.createOwnerProfile(user);
      onBootstrapSuccess();
    } catch (err: any) {
      setError(err?.message || 'Khởi tạo thất bại. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#020617] flex flex-col items-center justify-center p-6 text-white selection:bg-indigo-500/30">
      {/* Dynamic Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-600/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-600/10 rounded-full blur-[120px] animate-pulse animate-delay-2s" />
      </div>

      <div className="relative max-w-md w-full entrance-glass rounded-[2.5rem] border border-white/10 shadow-2xl p-10 text-center">
        <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
          <Sparkles className="text-white" size={40} />
        </div>
        <h1 className="text-2xl font-black text-white uppercase tracking-[0.2em] mb-3">Khởi động Hệ thống</h1>
        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest mb-8 leading-relaxed">{message}</p>
        
        {error && (
          <div className="mb-8 p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl">
            <p className="text-rose-400 text-xs font-black uppercase tracking-widest">{error}</p>
          </div>
        )}
        
        <button
          type="button"
          onClick={handleInit}
          disabled={loading}
          className="w-full h-16 rounded-2xl font-black text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-xl shadow-indigo-600/20 uppercase tracking-[0.2em] text-sm"
        >
          {loading ? 'Đang thiết lập thông số...' : 'Kích hoạt Hệ thống F&B'}
        </button>
        
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            className="mt-8 text-slate-600 hover:text-indigo-400 font-black text-[10px] uppercase tracking-[0.3em] transition-colors"
          >
            Đăng xuất an toàn
          </button>
        )}
      </div>
    </div>
  );
};

export default BootstrapScreen;
